import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Menu, Search, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { CATEGORIES, TOOLS } from '@/lib/tools';
import { NAV } from '@/constants/testIds';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function Header() {
    const location = useLocation();
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [openSheet, setOpenSheet] = useState(false);

    useEffect(() => {
        setOpenSheet(false);
    }, [location.pathname]);

    const suggestions = useMemo(() => {
        if (!q.trim()) return [];
        const needle = q.trim().toLowerCase();
        return TOOLS.filter(
            (t) =>
                t.title.toLowerCase().includes(needle) ||
                t.short.toLowerCase().includes(needle),
        ).slice(0, 5);
    }, [q]);

    const onSubmit = (e) => {
        e.preventDefault();
        if (suggestions[0]) {
            navigate(`/tools/${suggestions[0].slug}`);
            setQ('');
        } else {
            navigate(`/tools?q=${encodeURIComponent(q.trim())}`);
        }
    };

    return (
        <header className="sticky top-0 z-50 border-b border-border/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
            <div className="max-w-6xl mx-auto h-16 px-4 sm:px-6 lg:px-8 flex items-center gap-3">
                <Link
                    to="/"
                    data-testid={NAV.logo}
                    className="flex items-center gap-2 shrink-0"
                    aria-label="UtilityTool home"
                >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(var(--primary))] text-white shadow-sm">
                        <Sparkles className="w-4 h-4" strokeWidth={2.25} />
                    </span>
                    <span className="font-semibold text-slate-900 tracking-tight">
                        UtilityTool
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1 ml-4" aria-label="Primary">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-700 hover:text-slate-900"
                                data-testid={NAV.categoriesMenu}
                            >
                                Categories
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                            <DropdownMenuLabel>Browse by category</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {CATEGORIES.map((c) => (
                                <DropdownMenuItem
                                    key={c.id}
                                    onClick={() => navigate(`/tools?category=${c.id}`)}
                                    className="cursor-pointer"
                                >
                                    {c.label}
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate('/tools')} className="cursor-pointer">
                                All tools
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <NavLink
                        to="/tools"
                        className={({ isActive }) =>
                            `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                isActive
                                    ? 'text-slate-900 bg-slate-100'
                                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                            }`
                        }
                    >
                        Tools
                    </NavLink>
                    <NavLink
                        to="/blog"
                        className={({ isActive }) =>
                            `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                isActive
                                    ? 'text-slate-900 bg-slate-100'
                                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                            }`
                        }
                    >
                        Blog
                    </NavLink>
                    <NavLink
                        to="/about"
                        className={({ isActive }) =>
                            `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                isActive
                                    ? 'text-slate-900 bg-slate-100'
                                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                            }`
                        }
                    >
                        About
                    </NavLink>
                </nav>

                <form onSubmit={onSubmit} className="ml-auto flex-1 max-w-sm relative hidden sm:block" role="search">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid={NAV.searchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search tools…"
                        className="pl-9 h-9 bg-white border-border/80 focus-visible:ring-[hsl(var(--ring))]"
                        aria-label="Search tools"
                    />
                    {suggestions.length > 0 ? (
                        <div className="absolute z-40 mt-2 w-full rounded-lg border border-border bg-white shadow-lg overflow-hidden">
                            {suggestions.map((s) => (
                                <button
                                    key={s.slug}
                                    type="button"
                                    className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        navigate(`/tools/${s.slug}`);
                                        setQ('');
                                    }}
                                >
                                    <span className="font-medium text-slate-800">{s.title}</span>
                                    <span className="block text-xs text-slate-500 truncate">{s.short}</span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </form>

                <div className="hidden md:block">
                    <Button
                        data-testid={NAV.ctaBrowseTools}
                        onClick={() => navigate('/tools')}
                        className="h-9 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--link-hover))]"
                    >
                        Browse tools
                    </Button>
                </div>

                <div className="md:hidden ml-auto">
                    <Sheet open={openSheet} onOpenChange={setOpenSheet}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                data-testid={NAV.mobileMenuTrigger}
                                aria-label="Open menu"
                            >
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-80">
                            <SheetHeader>
                                <SheetTitle className="text-left">UtilityTool</SheetTitle>
                            </SheetHeader>
                            <div className="mt-4 flex flex-col gap-1">
                                <Link to="/tools" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm font-medium text-slate-800">All tools</Link>
                                <Link to="/blog" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm font-medium text-slate-800">Blog</Link>
                                <Link to="/about" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm font-medium text-slate-800">About</Link>
                                <Link to="/contact" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm font-medium text-slate-800">Contact</Link>
                                <div className="mt-3 mb-1 px-3 text-xs uppercase tracking-wide text-slate-500">Categories</div>
                                {CATEGORIES.map((c) => (
                                    <Link
                                        key={c.id}
                                        to={`/tools?category=${c.id}`}
                                        className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700"
                                    >
                                        {c.label}
                                    </Link>
                                ))}
                                <div className="mt-3 mb-1 px-3 text-xs uppercase tracking-wide text-slate-500">Legal</div>
                                <Link to="/privacy-policy" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700">Privacy Policy</Link>
                                <Link to="/terms-of-service" className="px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700">Terms of Service</Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
